/**
 * 디바이스 사진 그리드 피커 (앱 안 · web 렌더)
 *
 * 앱 네이티브 "업로드 방법 선택" 다이얼로그 대신, 디바이스의 최근 사진을 웹이 직접
 * 그리드로 그린다. 앱은 썸네일(base64 data URI)과 token(불투명 식별자)만 push 하고,
 * "사진 추가" 시 web 이 선택한 token 목록을 돌려주면 앱이 기존 업로드 파이프라인
 * (/api/upload) 으로 올린다.
 *
 * [브릿지 계약]
 *   web → app : listPhotos({limit, cursor, reselect})   → callbackPhotos
 *               uploadSelectedPhotos({type,tag,id,tokens}) → callbackUploadImage (기존 경로 재사용)
 *   app → web : EventBus 'app:photos' / 'upload:image'
 *   둘 다 레거시 폴백이 없는 신규 액션 — 구앱은 ok:0 이므로 네이티브 피커로 분기한다.
 *
 * [BS 싱글톤]
 *   BS 는 한 번에 하나의 target 만 갖는다. 이 피커가 열리면 DiaryPhotoHub 는 파괴되므로,
 *   커밋이 끝나면 data.hub 로 받은 옵션으로 허브를 다시 열어 목록을 새로 읽게 한다.
 *
 * [호출 — data 파라미터]  (도메인 무관 공용 컴포넌트 — 커밋/도메인 UI 는 호출부가 주입한다)
 *   $j.Ui.BS.open({
 *       target: '$j.Ui.DevicePicker',
 *       fixedHeight: true,
 *       data: {
 *           uploadTag:  '/api/upload tag',                  // 호출부 주입
 *           uploadId:   ordernum,                           // uploadSelectedPhotos/컨텍스트 id (없으면 '')
 *           context:    'memberProfile',                    // passthrough 일 때 호출부의 upload context
 *           categories: ['작업전','작업중','작업후'],  // 빈 배열/미지정이면 칩 행 자체를 숨긴다
 *           tag:        '작업전',                            // 초기 활성 칩 (categories 안의 값)
 *           title:      '사진 선택',                          // BS 헤더 타이틀
 *           max:        1,                                  // 다중선택 상한 (0/미지정=무제한, 1=단일선택)
 *           maxSheet:   10,                                 // 폴백/촬영 시 최대 장수 (기본 max → 10)
 *           onCommit:   (files, {tag,id}) => Promise,       // 업로드분 커밋을 호출부에 위임 (없으면 passthrough)
 *           footerHtml: '<...>',                            // 푸터에 끼울 호출부 HTML (예: 마케팅 동의)
 *           onFooterClick: (e, picker) => {},               // footerHtml 클릭 위임
 *           hub:        {…BS.open 옵션…},                    // 커밋 후 복귀할 허브 (없으면 시트 닫기)
 *           backClose:  true,                               // 사진 전체 허용 격자에서 안드로이드 뒤로가기로 닫기 — 크게 보기가 열려
 *                                                           //   있으면 크게 보기만, 아니면 감싼 시트(BS.enableBackClose). 부분·거부는 종전대로
 *           // ── 「작업사진 자동 찾기」(일지 현장사진만) ──
 *           range:      { since, until, label },            // 촬영시각 구간('YYYY-MM-DD HH:mm:ss') — 있으면 최근 격자 위에
 *                                                           //   「작업 시간대 사진 N장」 추천 구역을 따로 그린다 (listPhotos{since,until} 1회)
 *           excludeDates: ['2026-09-28 13:11:23', …],      // 이미 이 오더에 붙은 사진의 촬영시각 — 추천에서 뺀다
 *           suggestSkip: '…이유…',                          // 찾지 않기로 한 경우 — 추천 구역에 이유 한 줄만
 *       },
 *   });
 *
 * [추천 구역]  range 가 있으면 요청 순서는 ① listPhotos{since,until} → ② listPhotos(최근). ①의 응답에 `did` 가 없으면
 *   구간을 모르는 앱이다 — 그 응답을 최근 첫 페이지로 쓰고 추천 구역엔 "이 앱 버전은 지원하지 않아요"만 남긴다.
 *   추천에 든 사진은 최근 격자에서 빼고(한 장이 두 번 보이지 않게), 선택은 하지 않는다 — 올릴 사진은 회원이 체크한다
 *   (2026-10-23, CS 실장 의견). 3장 이상이면 촬영순으로 작업전/중/후를 제안한다(셀의 태그를 탭하면 순환). 커밋 콜백에 `ctx.tags[]`(파일별 태그)가 하나 더 간다.
 *
 * [커밋]
 *   onCommit 있음 — 업로드 완료분을 호출부 콜백에 넘긴다. 어느 도메인(오더 job-photos 등)에 저장할지는
 *                   호출부가 정한다. 피커는 콜백 Promise 가 끝나면 시트를 닫는다.
 *   onCommit 없음(passthrough) — 등록하지 않는다. `upload:image` 를 호출부 context 로 흘려보내
 *                   기존 핸들러(예: Pages/Mobile/SettingMy.js 의 memberProfile → /m/ajax/member/profile-image)가 처리한다.
 */
// 앱이 한 번에 보내는 썸네일 장수 (base64 payload 상한 고려). 격자가 3열이 되며(2026-10-27) 3칸×7줄로 맞췄다 —
// 칸이 커져 앱 썸네일도 키우므로(384px) 한 번에 넘기는 양을 전과 비슷하게 둔다
const PAGE_SIZE = 21;
const PREVIEW_TIMEOUT = 12000;  // 크게 보기 — previewPhoto 회신을 기다리는 시간(장당)
const PREVIEW_CACHE = 12;       // 크게 보기 — 받아 둔 큰 그림 장수 상한(오래된 것부터 버림)
const UPLOAD_CONTEXT = 'diaryDevicePicker';
const CALLBACK_TIMEOUT = 12000; // 앱이 수락(ok:1) 해놓고 콜백을 안 주는 경우의 안전장치
// 업로드는 목록조회와 달리 실제 전송이라 오래 걸린다 (10장 × LTE). 넉넉히 잡되 무한대기는 막는다.
const COMMIT_TIMEOUT = 90000;
const CAM_GUARD_MS = 15000;     // 카메라에서 돌아온 뒤 「사진 추가」를 막는 시간 — 앱은 카메라 취소를 알리지 않아 업로드 끝을 기다릴 수 없다

export default class DevicePicker {
    constructor() {
        // data 로 주입되는 호출자별 설정 (init 에서 확정)
        this.uploadTag = '';         // /api/upload tag (호출부 주입)
        this.uploadId = '';          // uploadSelectedPhotos / upload context 의 id (호출부 주입 — 오더번호 등)
        this.uploadContext = UPLOAD_CONTEXT; // upload:image 게이트용 context (passthrough 면 호출부 것)
        this.categories = [];        // 카테고리 칩 목록 (비어있으면 칩 행 숨김)
        this.maxSelect = 0;          // 다중선택 상한 (0=무제한)
        this.maxSheet = 10;

        this.activeTag = '';
        this._timer = null;
        this._commitTimer = null;    // uploadSelectedPhotos 응답 대기 (목록조회 타이머와 별도)
        this._resetState();
    }

    /**
     * 열 때마다 비우는 임시 상태. 이 피커는 싱글톤(app.js initModule)이라 destroy() 뒤에도 인스턴스가 남는다 —
     * 응답을 기다리는 중에 시트가 닫히면 loading 이 true 로 남고, 다음에 열 때 _requestRange/_requestPage 가
     * 첫 줄 가드에서 돌아가 「사진을 불러오는 중…」에 영영 멈춘다(2026-10-02 Flip 재현). init() 맨 앞에서 부른다.
     * 호출자 설정(uploadTag·categories 등)은 init 이 매번 덮어쓰므로 여기 없다.
     */
    _resetState() {
        this._clearTimer();
        this._clearCommitTimer();
        this.selected = new Map();   // token → item (재렌더 시 선택 보존)
        this.items = [];
        this.cursor = null;
        this.hasMore = false;
        this.loading = false;
        this.committing = false;
        this.permission = null;      // 'full' | 'partial' | 'denied'
        this.mode = 'grid';          // 'grid' | 'fallback'
        this.fallbackMsg = '';
        this._rendered = 0;          // 그리드에 이미 그린 items 개수 (페이지 추가 append 용)
        this._pendingReset = false;  // reselect 응답(첫 페이지) 대기 중 — 도착하면 그리드 리셋
        this._autoFills = 0;         // 화면이 안 채워져 자동으로 당겨온 페이지 수 (폭주 방지)
        this._received = false;      // callbackPhotos 를 한 번이라도 받았는지 (빈 상태 안내 조건)
        this._notFullNotified = false; // onNotFull(부분·거부 시 호출부 위임) 을 한 번만 부르기 위한 가드

        // 추천 구역(「작업사진 자동 찾기」)
        this.range = null;               // {since, until, label} — 있을 때만 구간 요청을 먼저 보낸다
        this.excludeDates = new Set();   // 기첨부 촬영시각(숫자만) — 추천에서 제외
        this.sg = null;                  // {state:'loading'|'found'|'empty'|'skip'|'unsupported', items, reason, excluded?, registered?}
        this._rangePending = false;      // 구간 요청의 응답을 기다리는 중
        this.suggestTokens = new Set();  // 추천에 든 token — 최근 격자에서 뺀다
        this.itemTags = new Map();       // token → 제안 태그(작업전·중·후). 추천 셀만. 셀에서 탭해 바꿀 수 있다
        this._commitTags = null;         // 커밋 시점의 token 순서별 태그 스냅샷 (업로드 회신 순서와 짝짓기)

        // 크게 보기(previewPhoto) — 큰 그림 대기열·캐시
        this._clearPreview();
    }

    // ─── 라이프사이클 ─────────────────────────────────
    init() {
        this._resetState();
        const d = this.data || {};
        this.uploadTag  = d.uploadTag || '';
        this.uploadId   = d.uploadId != null ? d.uploadId : '';
        // 커밋을 호출부에 위임하면(onCommit) 피커 전용 context 로, 아니면(passthrough) 호출부 context 로 결과를 흘려보낸다
        this.uploadContext = (typeof d.onCommit === 'function') ? UPLOAD_CONTEXT : (d.context || UPLOAD_CONTEXT);
        this.categories = Array.isArray(d.categories) ? d.categories.slice() : [];
        this.maxSelect  = Math.max(0, parseInt(d.max, 10) || 0);
        this.maxSheet   = parseInt(d.maxSheet, 10) || this.maxSelect || 10;
        // 칩이 없는 호출자(작업문서)는 분류 tag 없이 커밋한다
        this.activeTag  = this.categories.length
            ? (this.categories.includes(d.tag) ? d.tag : this.categories[0])
            : '';

        this.parent?.setTitle?.(d.title || '사진 선택');

        // 크게 보기 — 앱이 큰 사진(previewPhoto)을 줄 수 있을 때만. 썸네일(작은 그림)만 키우면 글자를 못 읽는다.
        // 지원하면 사진 = 크게 보기, 동그라미 = 선택. 모르는 앱(3.211 이하)은 지금처럼 누르면 선택
        this.canZoom = $j.F.App.photoPreviewSupported();

        // 추천 구역 — 구간이 있으면 '찾는 중', 호출부가 찾지 않기로 했으면 이유만, 둘 다 없으면 구역 없음
        const rg = d.range;
        if (rg && (rg.since || rg.until)) {
            this.range = { since: rg.since || null, until: rg.until || null, label: rg.label || '' };
            this.sg = { state: 'loading', items: [], reason: '' };
        } else if (d.suggestSkip) {
            this.sg = { state: 'skip', items: [], reason: String(d.suggestSkip) };
        }
        const norm = v => String(v || '').replace(/\D/g, '');
        this.excludeDates = new Set((Array.isArray(d.excludeDates) ? d.excludeDates : []).map(norm).filter(Boolean));

        this.draw();
        this.initHandler();
        this._subscribe();
        if (this.range) this._requestRange(); else this._requestPage(true);
    }

    /**
     * 구간 요청 — 추천 구역용 `listPhotos{since, until}` 한 번. 응답은 같은 callbackPhotos 로 오므로 _rangePending 으로 구분한다.
     * 미지원(구앱·PC)이면 구간 요청 없이 최근 격자 경로(_requestPage)가 폴백을 처리한다.
     */
    _requestRange() {
        if (!this.range || this.loading || this.committing) return;
        if (!$j.F.App.pickerSupported()) { this.sg = null; this._renderSuggest(); this._requestPage(true); return; }
        this.loading = true;
        this._rangePending = true;
        this.sg = { state: 'loading', items: [], reason: '' };
        this._renderSuggest();
        const accepted = $j.F.App.listPhotos({ limit: PAGE_SIZE, since: this.range.since, until: this.range.until });
        if (!accepted) {
            this.loading = false;
            this._rangePending = false;
            this.sg = { state: 'unsupported', items: [], reason: '' };
            this._renderSuggest();
            this._requestPage(true);
            return;
        }
        this._startTimer(true);
    }

    destroy() {
        this._clearTimer();
        this._clearCommitTimer();
        $j.Common.Util.loadingOverlay(false);   // 커밋 중 시트가 닫혀도 오버레이를 남기지 않는다
        $(`#${this.id}`).off();
        $j.Common.EventBus.offNamespace($j.getNS(this));
        // 싱글톤이라 참조를 비워야 다음에 열 때 다시 건다(남겨 두면 `if (!self._onCamReturn)` 가 건너뛴다)
        if (this._onCamReturn) document.removeEventListener('visibilitychange', this._onCamReturn);
        this._onCamReturn = null;
        this._clearPreview();
    }

    _subscribe() {
        const ns = $j.getNS(this);

        $j.Common.EventBus.on(ns, 'app:photos', (e, data) => this._onPhotos(data));
        $j.Common.EventBus.on(ns, 'app:photo:preview', (e, data) => this._onPreview(data));

        $j.Common.EventBus.on(ns, 'upload:image', (e, payload) => {
            // 같은 페이지의 다른 업로드 핸들러(DiaryJobs 등)와 섞이지 않도록 context 게이트
            if ($j.F.Upload.getContext() !== this.uploadContext) return;

            this._clearCommitTimer();   // 응답이 왔으니 무응답 안전장치 해제

            const files = (Array.isArray(payload) ? payload : [payload]).filter(f => f && f.id);
            this._onUploaded(files);
        });
    }

    // ─── 앱 통신 ─────────────────────────────────────
    /** 사진 한 페이지 요청(기본은 최근순). 구앱·웹 환경(ok:0)이면 네이티브 폴백으로 전환 */
    _requestPage(isFirst = false) {
        if (this.loading || this.committing) return;
        if (!isFirst && !this.hasMore) return;

        // 피커 지원 게이트: 미지원(구앱·미지원앱·PC웹)이면 listPhotos 를 아예 부르지 않고 바로 네이티브 폴백.
        // (옛앱은 listPhotos 에 1 을 주면서 자기 네이티브 피커를 여는 부작용이 있어, '떠보기' 자체가
        // 그리드+네이티브 이중노출을 만든다.) 판정은 부작용 없는 getCapabilities 브로커(App.pickerSupported).
        const pickerOk = $j.F.App.pickerSupported();
        if (!pickerOk) {
            if (isFirst) this._useNativeFallback(this._noApp()
                ? '앱에서만 사진 목록을 볼 수 있습니다. 파일을 직접 선택해 주세요.'
                : '이 버전에서는 기존 방식으로 선택합니다.', true);
            return;
        }

        this.loading = true;
        this._renderMore();

        const accepted = $j.F.App.listPhotos({ limit: PAGE_SIZE, cursor: isFirst ? null : this.cursor });
        if (!accepted) {
            this.loading = false;
            if (isFirst) this._useNativeFallback(this._noApp()
                ? '앱에서만 사진 목록을 볼 수 있습니다. 파일을 직접 선택해 주세요.'
                : '이 버전에서는 사진 목록을 불러올 수 없습니다.', true);
            else { this.hasMore = false; this._renderMore(); }
            return;
        }
        this._startTimer(isFirst);
    }

    _onPhotos(data) {
        this._clearTimer();
        this.loading = false;
        this._received = true;

        const payload = data || {};
        const status = payload.status || 'ok';
        this.permission = payload.permission || null;

        // 구간 요청(추천 구역)의 응답 — did 가 있어야 필터가 걸린 목록이다(§11-6)
        if (this._rangePending) {
            this._rangePending = false;
            const full = !this.permission || this.permission === 'full';
            if (full && payload.did && typeof payload.did === 'object') {
                const norm = v => String(v || '').replace(/\D/g, '');
                const raw = (Array.isArray(payload.items) ? payload.items : []).filter(it => it && it.token);
                const items = raw
                    .filter(it => !this.excludeDates.has(norm(it.date)))
                    .sort((a, b) => String(a.date || '').localeCompare(String(b.date || '')));
                // 기첨부로 거른 장수도 들고 간다 — 전부 등록된 뒤에 "찍은 사진이 없어요" 라고 하면 틀린 말이 된다(10/2 영상)
                const excluded = raw.length - items.length;
                this.sg = items.length
                    ? { state: 'found', items, reason: '', excluded }
                    : { state: 'empty', items: [], reason: '', excluded };
                this._applySuggest();
                this._requestPage(true);            // 이어서 최근 격자 첫 페이지
                return;
            }
            // did 없음 = 구간을 모르는 앱(전체 목록이 왔다) → 그 응답을 최근 첫 페이지로 쓰고 추천 구역엔 미지원만 표시.
            // 전체허용이 아니면 추천 자체가 의미 없다 — 구역을 걷고 아래 onNotFull 경로에 맡긴다.
            this.sg = full ? { state: 'unsupported', items: [], reason: '' } : null;
            this._renderSuggest();
        }

        // reselect 의 응답은 언제나 '첫 페이지' — 누적분을 버리고 처음부터 다시 그린다
        let rerunRange = false;
        if (this._pendingReset) {
            this._pendingReset = false;
            this.items = [];
            this.selected.clear();
            this.cursor = null;
            this._rendered = 0;
            this._autoFills = 0;
            // 권한이 바뀌었으니 추천 구역도 새로 — 이 첫 페이지를 그린 뒤 구간 요청을 한 번 더 보낸다
            if (this.range) { this.suggestTokens = new Set(); this.itemTags.clear(); rerunRange = true; }
        }

        // 호출부가 '전체허용이 아닐 때 다른 화면'을 원하면(onNotFull) 격자를 그리지 않고 그쪽에 위임한다.
        // (일지 현장사진·작업문서: 부분·거부면 기존 패널+배너로 전환.) DevicePicker 는 곧 파괴되므로 1회만 알린다.
        if (this.permission && this.permission !== 'full'
                && typeof this.data?.onNotFull === 'function' && !this._notFullNotified) {
            this._notFullNotified = true;
            this.data.onNotFull(this.permission);
            return;
        }

        // 사진 전체 허용 격자 — 뒤로가기로 감싼 시트를 닫는다(호출부가 data.backClose 를 줬을 때만, 2026-10-27).
        // 부분·거부(옛 칸 화면)는 위 onNotFull 로 넘어가 여기 오지 않는다 — 종전대로 일지 화면을 벗어난다
        if (this.data?.backClose && this.permission === 'full') $j.Ui.BS.enableBackClose();

        // (onNotFull 없는 호출자 — 프로필·서류) 거부(denied)도 '전체허용 아님'으로 취급: 아래 격자 렌더가
        // 부분접근과 똑같이 '업로드' 버튼을 그리고 _renderPerm 이 접근 허용 배너를 띄운다.
        // (네이티브 업로드는 READ_MEDIA 권한 없이도 동작하므로 거부 상태에서도 올릴 수 있다.)

        const prevCursor = this.cursor;
        this.cursor = payload.cursor ?? null;

        const incoming = Array.isArray(payload.items) ? payload.items : [];
        const seen = new Set(this.items.map(i => i.token));
        const fresh = incoming.filter(it => it && it.token && !seen.has(it.token));
        this.items = this.items.concat(fresh);

        // 페이지 끝 판단은 오직 hasMore — 썸네일 생성 실패로 items.length < limit 이어도 다음 페이지는 있다.
        // 다만 커서가 전진하지 않은 채 새 항목도 0건이면(앱 버그) 같은 요청을 무한 반복하므로 거기서 멈춘다.
        this.hasMore = !!payload.hasMore;
        if (this.hasMore && fresh.length === 0 && this.cursor === prevCursor) this.hasMore = false;

        if (this.mode === 'fallback') {
            // 타임아웃으로 폴백 화면에 갔는데 콜백이 뒤늦게 도착한 경우 — 그리드로 복귀
            this.mode = 'grid';
            this.draw();
        } else if (this._rendered === 0) {
            this._renderGrid();          // 첫 페이지 · 재선택 후 — 전체 렌더
        } else {
            this._appendCells(fresh);    // 추가 페이지 — 셀만 append (스크롤 위치 보존)
        }
        this._renderPerm();
        this._renderMore();
        this._renderFooter();
        if (rerunRange) { this._requestRange(); return; }
        this._autoFill();
    }

    /** 추천 결과 반영 — 태그 제안을 잡고 추천 구역을 그린다(미리 선택은 하지 않는다). 최근 격자는 그릴 때 추천분을 뺀다 */
    _applySuggest() {
        const sg = this.sg;
        for (const t of this.suggestTokens) { this.selected.delete(t); this.itemTags.delete(t); }
        this.suggestTokens = new Set();
        if (sg && sg.state === 'found') {
            this.suggestTokens = new Set(sg.items.map(it => it.token));
            this._proposeTags(sg.items);
        }
        this._renderSuggest();
        if (this.mode !== 'fallback' && this._rendered > 0) this._renderGrid();
        this._renderFooter();
    }

    /**
     * 작업전·중·후 제안 — 촬영시각 순 첫 장=작업전, 마지막=작업후, 나머지=작업중.
     * **제안일 뿐**이다(회원이 확정하기 전까지 저장하지 않는다). 3장 미만이거나 분류가 3종이 아니면 제안하지 않는다
     * — 틀린 태그가 붙는 것보다 비어 있는 편이 낫다.
     */
    _proposeTags(items) {
        this.itemTags.clear();
        if (this.categories.length !== 3 || items.length < 3) return;
        const [first, during, last] = this.categories;
        items.forEach((it, i) => {
            this.itemTags.set(it.token, i === 0 ? first : (i === items.length - 1 ? last : during));
        });
    }

    /** token → 항목 (추천 구역 먼저, 그다음 최근 항목) */
    _findItem(token) {
        const sgHit = this.sg && this.sg.state === 'found' ? this.sg.items.find(i => i.token === token) : null;
        return sgHit || this.items.find(i => i.token === token) || null;
    }

    _renderSuggest() {
        const $box = $(`#${this.id}`).find('.ddp-suggest');
        if (!$box.length) return;
        const sg = this.sg;
        if (!sg) { $box.empty(); return; }
        const label = this.range && this.range.label ? this._esc(this.range.label) + ' · ' : '';

        if (sg.state === 'loading') {
            $box.html(`
                <div class="ddp-sg is-loading">
                    <div class="ddp-sg-title"><span class="ddp-sg-spin"></span>작업 시간대 사진을 찾는 중…</div>
                    <div class="ddp-sg-sub">${label}사진첩에서 이 작업 시간대에 찍은 사진만 찾고 있어요</div>
                </div>`);
            return;
        }
        if (sg.state === 'empty') {
            // 세 경우를 가른다 — 방금 다 올림 / 전부 이미 등록돼 있음 / 정말 없음
            const reg = sg.registered || 0;
            const ex  = sg.excluded || 0;
            let title, sub;
            if (reg > 0) {
                title = `작업 시간대 사진 ${reg}장을 등록했어요`;
                sub   = `${label}이 시간대에 더 올릴 사진이 없어요. 새로 찍은 사진은 아래 최근 사진에서 골라 주세요`;
            } else if (ex > 0) {
                title = `작업 시간대 사진 ${ex}장은 이미 등록되어 있어요`;
                sub   = `${label}새로 찍은 사진이 있으면 아래 최근 사진에서 골라 주세요`;
            } else {
                title = '이 작업 시간대에 찍은 사진이 없어요';
                sub   = `${label}아래 최근 사진에서 직접 골라 주세요`;
            }
            $box.html(`
                <div class="ddp-sg is-empty">
                    <div class="ddp-sg-title">${title}</div>
                    <div class="ddp-sg-sub">${sub}</div>
                </div>`);
            return;
        }
        if (sg.state === 'skip' || sg.state === 'unsupported') {
            const reason = sg.state === 'unsupported' ? '이 앱 버전은 작업 시간대 찾기를 지원하지 않아요' : (sg.reason || '');
            $box.html(`
                <div class="ddp-sg is-skip">
                    <div class="ddp-sg-title">자동 찾기를 하지 않았어요</div>
                    <div class="ddp-sg-sub">${this._esc(reason)}</div>
                </div>`);
            return;
        }
        const items = sg.items || [];
        const n = items.length;
        const over = (this.maxSelect > 0 && n > this.maxSelect) ? ` <small>(한 번에 ${this.maxSelect}장까지)</small>` : '';
        // 등록·제외 안내는 제목이 아니라 아래 줄에 — 제목은 flex 라 글자 덩어리가 늘면 세 줄로 쪼개진다(10/2 Flip)
        const ex = sg.excluded || 0;
        const reg = sg.registered || 0;
        const sub = reg > 0
            ? `방금 ${reg}장 등록 · ${label}남은 ${n}장 중 추가할 사진을 체크해 주세요`
            : (ex > 0
                ? `이미 등록된 ${ex}장 제외 · ${label}추가할 사진을 체크해 주세요`
                : `${label}추가할 사진을 체크해 주세요`);
        $box.html(`
            <div class="ddp-sg is-found">
                <div class="ddp-sg-title">작업 시간대 사진 <b>${n}</b>장을 찾았어요${over}</div>
                <div class="ddp-sg-sub">${sub}</div>
                <div class="ddp-grid ddp-sg-grid">${items.map(it => this._cellHtml(it, true)).join('')}</div>
                <div class="ddp-sg-note">이 작업 시간대만 조회 · 제안만 함 · 고른 사진만 업로드 · 설정에서 끌 수 있음</div>
            </div>`);
    }

    /** 'yyyy-MM-dd HH:mm:ss' → 'HH:mm' (추천 셀 하단 촬영시각) */
    _timeOf(date) {
        const m = /(\d{2}):(\d{2}):\d{2}$/.exec(String(date || ''));
        return m ? `${m[1]}:${m[2]}` : '';
    }

    /**
     * 그리드가 화면을 다 못 채우면 스크롤이 안 생겨 페이지네이션이 영영 안 걸린다
     * (썸네일 실패로 한 페이지가 거의 비는 경우 등). hasMore 가 남아있으면 자동으로 다음 페이지를 당긴다.
     */
    _autoFill() {
        if (!this.hasMore || this.loading || this.mode === 'fallback') return;
        if (this._autoFills >= 8) return;
        const el = $(`#${this.id}`).find('.ddp-body')[0];
        if (!el || el.scrollHeight > el.clientHeight + 40) return;   // 이미 스크롤 가능하면 사용자 스크롤에 맡긴다
        this._autoFills++;
        this._requestPage(false);
    }

    /**
     * 앱 밖(PC·폰 브라우저)인가 — **구앱과 구별하려고** 직접 본다.
     *
     * callBroker 는 '앱 없음' 과 '구앱 미구현' 을 똑같이 `ok:0` 으로 돌려주므로, 그것만 보고
     * 안내문을 쓰면 PC 사용자에게 "이 버전에서는…" 이라는 앱 버전 탓으로 읽힌다.
     * (대리진입은 isApp() 이 false 여도 window.App 은 살아 있어 '앱 밖' 이 아니다 — 여기선 그게 맞다)
     */
    _noApp() { return !window.App; }

    /** 구앱·권한거부·앱밖 → 기존 네이티브 피커(또는 PC 파일선택)로 폴백. 업로드 결과는 그대로 _onUploaded(→onCommit) 로 흐른다. */
    _useNativeFallback(msg, autoOpen) {
        this.mode = 'fallback';
        this.fallbackMsg = msg || '';
        this.draw();
        if (autoOpen) this._openNativePicker();
    }

    _openNativePicker() {
        $j.F.Upload.addPhotoHere({
            tag: this.uploadTag,
            context: this.uploadContext,
            max_sheet: this.maxSheet,
            id: this.uploadId,
        });
    }

    _startTimer(isFirst) {
        this._clearTimer();
        this._timer = setTimeout(() => {
            if (!this.loading) return;
            this.loading = false;
            this._pendingReset = false;   // 재선택 다이얼로그를 취소했을 수도 있다 — 리셋 예약 해제
            if (isFirst && this.items.length === 0) {
                this._useNativeFallback('앱이 사진 목록을 보내지 않았습니다.', false);
            } else {
                this.hasMore = false;
                this._renderMore();
            }
        }, CALLBACK_TIMEOUT);
    }

    _clearTimer() {
        if (this._timer) { clearTimeout(this._timer); this._timer = null; }
    }

    /**
     * 커밋(uploadSelectedPhotos) 응답 대기 타이머.
     *
     * 앱이 `ok:1` 로 수락해놓고 업로드에 실패하거나 프로세스가 죽으면 callbackUploadImage 가
     * 오지 않는다 (.ai/file-upload-protocol.md '실패/취소'). 이 안전장치가 없으면
     * loadingOverlay(z-index 최상위 전면 차단)가 영영 남아 새로고침 말고는 빠져나갈 수 없다.
     *
     * 선택은 지우지 않는다 — 사용자가 그대로 [사진 추가] 를 다시 누를 수 있게.
     * 뒤늦게 콜백이 오면 그때 정상 커밋되고 허브로 돌아간다 (사진을 잃는 것보다 낫다).
     */
    _startCommitTimer() {
        this._clearCommitTimer();
        this._commitTimer = setTimeout(() => {
            this._commitTimer = null;
            if (!this.committing) return;
            this.committing = false;
            $j.Common.Util.loadingOverlay(false);
            this._renderFooter();
            $j.Ui.Toast.show('warning', '업로드 응답이 없습니다. 다시 시도해 주세요.');
        }, COMMIT_TIMEOUT);
    }

    _clearCommitTimer() {
        if (this._commitTimer) { clearTimeout(this._commitTimer); this._commitTimer = null; }
    }

    // ─── 렌더링 ──────────────────────────────────────
    draw() {
        const html = `
        <div class="ddp-wrap${this.canZoom ? ' is-zoom' : ''}">
            <div class="ddp-head">
                ${this.categories.length ? `<div class="ddp-chips">${this._chipsHtml()}</div>` : ''}
                ${this.sg ? '' : `<div class="ddp-source${this.categories.length ? '' : ' is-first'}">최근 항목 <span class="ddp-source-caret">▾</span></div>`}
            </div>
            <div class="ddp-body">
                ${this.mode === 'fallback' ? this._fallbackHtml() : `
                <div class="ddp-suggest"></div>
                ${this.sg ? '<div class="ddp-source is-body">최근 사진 <span class="ddp-source-caret">▾</span></div>' : ''}
                <div class="ddp-perm"></div>
                <div class="ddp-grid"></div>
                <div class="ddp-more"></div>`}
            </div>
            <div class="ddp-foot">
                <div class="ddp-foot-extra">${this.data?.footerHtml || ''}</div>
                <div class="ddp-btns">
                    <button type="button" class="ddp-btn ddp-btn-cancel">닫기</button>
                    <button type="button" class="ddp-btn ddp-btn-submit">사진 추가</button>
                </div>
            </div>
        </div>`;

        $(`#${this.id}`).html(html);
        this._rendered = 0;
        if (this.mode !== 'fallback') { this._renderSuggest(); this._renderPerm(); this._renderGrid(); this._renderMore(); }
        this._renderFooter();
        this._bindScroll();
    }

    /**
     * 페이지네이션 스크롤 — scroll 이벤트는 버블링하지 않아 위임(delegate)이 안 먹는다.
     * draw() 로 .ddp-body 가 새로 만들어질 때마다 직접 바인딩한다.
     */
    _bindScroll() {
        const self = this;
        $(`#${this.id}`).find('.ddp-body').off('scroll.ddp').on('scroll.ddp', function () {
            if (self.loading || !self.hasMore) return;
            if (this.scrollTop + this.clientHeight >= this.scrollHeight - 120) self._requestPage(false);
        });
    }

    _chipsHtml() {
        return this.categories.map(t => `
            <button type="button" class="ddp-chip${t === this.activeTag ? ' is-on' : ''}" data-tag="${this._esc(t)}">${this._esc(t)}</button>
        `).join('');
    }

    _fallbackHtml() {
        return `
        <div class="ddp-empty">
            <div class="ddp-empty-ico">🖼</div>
            <div class="ddp-empty-msg">${this._esc(this.fallbackMsg || '사진 목록을 불러올 수 없습니다.')}</div>
            <button type="button" class="ddp-empty-btn ddp-native">${this._noApp() ? '파일 선택' : '기존 방식으로 선택'}</button>
        </div>`;
    }

    /** 그리드 전체 렌더 (첫 페이지 · 칩 전환) */
    _renderGrid() {
        const $grid = $(`#${this.id}`).find('.ddp-grid').not('.ddp-sg-grid');
        if (!$grid.length) return;

        // 전체허용(full)이 아니면 — 디바이스 사진 격자(촬영+최근사진) 대신 '업로드' 버튼만.
        // 부분접근에선 최근사진을 온전히 못 보여주므로, 예전처럼 네이티브 업로드 창을 열게 한다.
        // ('모두 허용으로 변경' 배너는 _renderPerm 이 이 위에 함께 그린다.)
        if (this.permission && this.permission !== 'full') {
            $grid.addClass('ddp-grid-upload').html(`
                <div class="ddp-upload-cell" role="button" aria-label="업로드">
                    <span class="ddp-upload-plus">+</span>
                    <span class="ddp-upload-txt">업로드</span>
                </div>`);
            this._rendered = 0;
            $(`#${this.id}`).find('.ddp-empty-inline').remove();
            return;
        }
        $grid.removeClass('ddp-grid-upload');

        const cam = `
            <div class="ddp-cell ddp-cam" role="button" aria-label="촬영">
                <span class="ddp-cam-ico">📷</span>
                <span class="ddp-cam-txt">촬영</span>
            </div>`;
        // 추천 구역에 든 사진은 여기서 뺀다 — 한 장이 두 번 보이지 않게
        const rest = this.items.filter(it => !this.suggestTokens.has(it.token));
        $grid.html(cam + rest.map(it => this._cellHtml(it)).join(''));
        this._rendered = this.items.length;

        // 빈 상태 안내는 "앱이 empty 를 확정했을 때"만 — 첫 응답 전이나 다음 페이지가 남아있을 땐 띄우지 않는다
        $(`#${this.id}`).find('.ddp-empty-inline').remove();
        if (this._received && !this.items.length && !this.loading && !this.hasMore && this.mode !== 'fallback') {
            $grid.after(`<div class="ddp-empty ddp-empty-inline"><div class="ddp-empty-msg">최근 사진이 없습니다.</div></div>`);
        }
    }

    /** 추가 페이지 셀만 append (스크롤 위치 유지) */
    _appendCells(items) {
        if (!items.length) return;
        const $grid = $(`#${this.id}`).find('.ddp-grid').not('.ddp-sg-grid');
        if (!$grid.length) return;
        $grid.append(items.filter(it => !this.suggestTokens.has(it.token)).map(it => this._cellHtml(it)).join(''));
        this._rendered = this.items.length;
    }

    /** @param {boolean} inSuggest 추천 구역 셀 — 촬영시각·제안 태그는 여기에만 붙는다 (최근 격자는 종전 모습) */
    _cellHtml(it, inSuggest = false) {
        const picked = this.selected.has(it.token);
        const order = picked ? this._selectedOrder(it.token) : '';
        const time = inSuggest ? this._timeOf(it.date) : '';
        const tag = inSuggest ? (this.itemTags.get(it.token) || '') : '';
        return `
            <div class="ddp-cell ddp-photo${picked ? ' is-picked' : ''}" data-token="${this._esc(it.token)}">
                <img src="${this._esc(it.thumb || '')}" alt="">
                ${tag ? `<span class="ddp-tag" data-token="${this._esc(it.token)}">${this._esc(tag)}</span>` : ''}
                ${time ? `<span class="ddp-date">${this._esc(time)}</span>` : ''}
                <span class="ddp-pick">${order}</span>
            </div>`;
    }

    /**
     * 부분접근("제한된 액세스 허용") 안내 배너 — 그리드 맨 위.
     *
     * 사용자가 권한창에서 "제한된 액세스 허용"을 고르면 본인이 고른 몇 장만 그리드에 보여
     * "사진이 없다"고 오해하기 쉽다. 접근 가능한 장수를 밝히고 '모두 허용'으로 유도한다.
     * 접근 가능한 사진이 적을수록(< 8장) 강조 스타일로 키운다.
     */
    _renderPerm() {
        const $perm = $(`#${this.id}`).find('.ddp-perm');
        if (!$perm.length) return;

        // 부분접근 — 일부만 보이니 '모두 허용' 유도
        if (this.permission === 'partial') {
            const n = this.items.length;
            const strong = n < 8;   // 거의 비어 보이는 상태 — 더 크게
            $perm.html(`
                <div class="ddp-perm-box${strong ? ' is-strong' : ''}">
                    <div class="ddp-perm-txt">
                        <b>⚠️ 일부 사진만 접근 중이에요.</b>
                        <span>지금 보이는 ${n}장은 이전에 직접 고른 사진입니다.
                        모든 사진을 보려면 '모두 허용'으로 변경하세요.</span>
                    </div>
                    <button type="button" class="ddp-perm-btn ddp-pick-more">모두 허용으로 변경</button>
                </div>`);
            return;
        }

        // 거부 — 접근 허용하면 격자로 바로 고를 수 있음 (지금은 아래 '업로드'로 올리기)
        if (this.permission === 'denied') {
            $perm.html(`
                <div class="ddp-perm-box is-strong">
                    <div class="ddp-perm-txt">
                        <b>⚠️ 사진 접근이 꺼져 있어요.</b>
                        <span>사진 접근을 허용하면 최근 사진을 바로 골라 올릴 수 있어요.
                        지금은 아래 '업로드'로 올릴 수 있습니다.</span>
                    </div>
                    <button type="button" class="ddp-perm-btn ddp-pick-more">사진 접근 허용</button>
                </div>`);
            return;
        }

        $perm.empty();
    }

    /** 하단 "더 불러오는 중" 안내줄 (부분접근 유도는 상단 배너가 담당) */
    _renderMore() {
        const $more = $(`#${this.id}`).find('.ddp-more');
        if (!$more.length) return;
        $more.html(this.loading ? `<div class="ddp-more-txt">사진을 불러오는 중…</div>` : '');
    }

    /** 선택 개수에 따른 버튼 상태 */
    _renderFooter() {
        const n = this.selected.size;
        const $btn = $(`#${this.id}`).find('.ddp-btn-submit');
        if (!$btn.length) return;
        // 전체허용 아님(부분접근) — 격자 선택이 없고 '업로드' 버튼이 네이티브로 바로 올리므로 '사진 추가' 숨김
        if (this.permission && this.permission !== 'full') { $btn.hide(); return; }
        $btn.show();
        // 추천 셀(셀마다 제안 태그)이 섞여 있으면 켜진 칩으로 다 들어가는 게 아니므로 칩 이름을 붙이지 않는다
        const anySuggested = Array.from(this.selected.keys()).some(t => this.itemTags.has(t));
        $btn.text(n > 0 ? `${anySuggested ? '사진 추가' : this._addLabel()} (${n})` : '사진 추가')
            .toggleClass('is-on', n > 0)
            .prop('disabled', n === 0 || this.committing);
    }

    /**
     * 「사진 추가」 버튼 글자 — 칩이 있으면 켜진 칩 이름을 붙인다("작업후로 추가"). 고른 사진이 켜진 칩으로 들어가는데
     * 칩을 안 바꿔 전부 첫 칩(작업전)으로 들어가는 일이 잦아서(3.21, 2026-10-26). 칩이 없는 호출자(서류·프로필)는 종전 그대로.
     * 추천 셀이 선택에 섞여 있으면(셀별 태그) _renderFooter 가 이 글자를 쓰지 않는다.
     */
    _addLabel() {
        const t = this.activeTag;
        if (!this.categories.length || !t) return '사진 추가';
        const code = t.charCodeAt(t.length - 1) - 0xAC00;
        const jong = (code >= 0 && code < 11172) ? code % 28 : 0;      // 받침 없음(0)·ㄹ받침(8) → '로', 그 외 → '으로'
        return `${t}${(jong === 0 || jong === 8) ? '로' : '으로'} 추가`;
    }

    _selectedOrder(token) {
        let i = 0;
        for (const key of this.selected.keys()) { i++; if (key === token) return i; }
        return '';
    }

    /** 셀 선택/해제 — 동그라미·옛 앱의 셀 클릭·크게 보기의 「이 사진 선택」이 같이 쓴다. 상한 1 이면 교체, 그 외 상한 초과는 막는다 */
    _togglePick(token) {
        if (this.selected.has(token)) {
            this.selected.delete(token);
        } else {
            if (this.maxSelect === 1) this.selected.clear();
            else if (this.maxSelect > 0 && this.selected.size >= this.maxSelect) {
                $j.Ui.Toast.show('warning', `최대 ${this.maxSelect}장까지 선택할 수 있습니다.`);
                return;
            }
            const item = this._findItem(token);
            if (item) this.selected.set(token, item);
        }
        this._repaintPicks();
    }

    // ─── 크게 보기 ────────────────────────────────────
    /**
     * 사진을 누르면 크게 보기(앱이 photoPreview 를 알 때만 — 모르는 앱은 누르면 선택). 2026-10-27 대표 선택(시험판 ①).
     * 누른 칸이 속한 구역(추천 / 최근)의 사진을 공통 뷰어($j.Ui.ZoomPhoto)에 아래 고정 줄(dock)로 띄운다 —
     * 좌우로 넘기며 아래 「이 사진 선택」으로 고르고(창은 그대로), 칸의 동그라미는 크게 안 보고 바로 고른다.
     * 처음엔 격자의 작은 그림을 띄우고, 앱이 큰 그림(긴 변 1600px)을 주면 갈아 끼운다(_wantPreview).
     */
    _openViewer(token) {
        if (!token || this.committing) return;
        const inSg = !!(this.sg && this.sg.state === 'found' && this.sg.items.some(it => it.token === token));
        const list = inSg ? this.sg.items.slice() : this.items.filter(it => !this.suggestTokens.has(it.token));
        const index = list.findIndex(it => it.token === token);
        if (index < 0) return;
        this._view = { list };
        $j.Ui.ZoomPhoto.open(null, {
            items: list.map(it => ({ url: this._pv.cache.get(it.token) || it.thumb, caption: this._shotLabel(it.date), token: it.token })),
            index,
            loop: false,
            dock: true,
            backClose: !!this.data?.backClose,   // 뒤로가기 한 번 = 크게 보기만 닫기(시트는 그대로)
            actions: [{
                label: item => (this.selected.has(item.token) ? '✓ 선택됨' : '이 사진 선택'),
                pressed: item => this.selected.has(item.token),
                onClick: item => this._togglePick(item.token),
            }],
            onShow: (item, i) => this._wantPreview(i),
        });
    }

    /** 'yyyy-MM-dd HH:mm:ss' → '10/26 16:45 촬영' (크게 보기 아래 줄) */
    _shotLabel(date) {
        const m = /\d{4}-(\d{2})-(\d{2}) (\d{2}):(\d{2})/.exec(String(date || ''));
        return m ? `${+m[1]}/${+m[2]} ${m[3]}:${m[4]} 촬영` : '';
    }

    /**
     * 큰 그림 받기 — 지금 보는 장 → 다음 → 이전 순서로 한 장씩(앱은 한 번에 하나를 만든다).
     * 넘길 때마다 대기열을 새로 세운다(지나간 사진은 버림). 받은 것·실패한 것·받는 중인 것은 다시 묻지 않는다.
     */
    _wantPreview(i) {
        const list = this._view?.list || [];
        const pv = this._pv;
        pv.queue = [i, i + 1, i - 1]
            .filter(k => k >= 0 && k < list.length)
            .map(k => list[k].token)
            .filter(t => !pv.cache.has(t) && !pv.failed.has(t) && t !== pv.busy);
        this._pumpPreview();
    }

    _pumpPreview() {
        const pv = this._pv;
        while (!pv.busy && pv.queue.length) {
            const token = pv.queue.shift();
            if (!$j.F.App.previewPhoto(token)) { pv.failed.add(token); continue; }
            pv.busy = token;
            pv.timer = setTimeout(() => {   // 회신이 안 오면 작은 그림 그대로 두고 다음 장으로
                pv.failed.add(token);
                this._previewDone();
            }, PREVIEW_TIMEOUT);
        }
    }

    _onPreview(data) {
        const d = data || {};
        const pv = this._pv;
        if (!pv.busy || String(d.token || '') !== pv.busy) return;   // 늦게 온 회신
        const token = pv.busy;
        if (d.url) {
            pv.cache.set(token, d.url);
            if (pv.cache.size > PREVIEW_CACHE) pv.cache.delete(pv.cache.keys().next().value);   // 오래된 것부터
            $j.Ui.ZoomPhoto.updateItem(it => it.token === token, { url: d.url });
        } else {
            pv.failed.add(token);   // 실패는 작은 그림 그대로(안내 없음)
        }
        this._previewDone();
    }

    _previewDone() {
        const pv = this._pv;
        if (pv.timer) { clearTimeout(pv.timer); pv.timer = null; }
        pv.busy = null;
        this._pumpPreview();
    }

    /** 크게 보기 정리 — 대기열·타이머·받아 둔 그림. 시트가 닫히거나 다시 열릴 때 부른다 */
    _clearPreview() {
        if (this._pv?.timer) clearTimeout(this._pv.timer);
        this._pv = { cache: new Map(), failed: new Set(), queue: [], busy: null, timer: null };
        this._view = null;
    }

    /** 선택 서클의 번호를 전부 다시 매긴다 (중간 해제 시 번호가 밀리므로) */
    _repaintPicks() {
        const $el = $(`#${this.id}`);
        $el.find('.ddp-photo').each((i, el) => {
            const $c = $(el);
            const token = String($c.data('token'));
            const picked = this.selected.has(token);
            $c.toggleClass('is-picked', picked);
            $c.find('.ddp-pick').text(picked ? this._selectedOrder(token) : '');
        });
        this._renderFooter();
    }

    // ─── 이벤트 ──────────────────────────────────────
    initHandler() {
        const $el = $(`#${this.id}`);
        const self = this;

        $el.off();

        // 카테고리 칩 — 최근 격자 쪽 선택은 활성 칩 태그로 커밋되므로 전환 시 비운다. 추천 셀은 자기 제안 태그로 가므로 지킨다.
        $el.on('click', '.ddp-chip', function () {
            const tag = String($(this).data('tag'));
            if (tag === self.activeTag) return;
            self.activeTag = tag;
            for (const t of Array.from(self.selected.keys())) { if (!self.suggestTokens.has(t)) self.selected.delete(t); }
            $el.find('.ddp-chip').removeClass('is-on');
            $(this).addClass('is-on');
            self._repaintPicks();
        });

        // (추천 구역) 셀의 제안 태그를 탭하면 작업전 → 작업중 → 작업후 순으로 바꾼다. 셀 선택과는 별개.
        $el.on('click', '.ddp-tag', function (e) {
            e.stopPropagation();
            const token = String($(this).data('token'));
            const cats = self.categories;
            if (!cats.length) return;
            const next = cats[(cats.indexOf(self.itemTags.get(token)) + 1) % cats.length];
            self.itemTags.set(token, next);
            $(this).text(next);
        });

        // 동그라미 — 크게 보기를 쓰는 앱에서는 동그라미만 선택/해제한다(사진 자체를 누르면 크게 보기).
        // 셀 클릭으로 번지지 않게 막는다(.ddp-tag 와 같은 꼴). 옛 앱은 아래 셀 클릭이 그대로 처리한다
        $el.on('click', '.ddp-pick', function (e) {
            if (!self.canZoom) return;
            e.stopPropagation();
            self._togglePick(String($(this).closest('.ddp-photo').data('token')));
        });

        // 사진 셀 — 크게 보기를 쓰는 앱이면 크게 보기, 옛 앱(3.211 이하)은 지금처럼 선택/해제
        $el.on('click', '.ddp-photo', function () {
            const token = String($(this).data('token'));
            if (self.canZoom) self._openViewer(token);
            else self._togglePick(token);
        });

        // 촬영 셀 — 기존 upload 액션 재사용 (from:'C')
        $el.on('click', '.ddp-cam', function () {
            // 촬영 업로드 진행 표시 — _commitSelected 가 겹쳐 누르기를 막는다. 카메라 앱에 있는 동안은 어차피 못 누르므로
            // 웹뷰가 다시 보이는 순간(카메라에서 돌아옴)부터 센다. 그 이벤트가 안 오는 기기는 누른 시각 기준으로 남는다.
            self._camAt = Date.now();
            if (!self._onCamReturn) {
                self._onCamReturn = () => { if (!document.hidden && self._camAt) self._camAt = Date.now(); };
                document.addEventListener('visibilitychange', self._onCamReturn);
            }
            $j.F.Upload.addPhotoHere({
                tag: self.uploadTag,
                context: self.uploadContext,
                max_sheet: 1,
                from: 'C',
                id: self.uploadId,
            });
        });

        // 업로드 셀(부분접근일 때만 노출) — from 없이 네이티브 업로드 창(촬영/갤러리 선택)을 연다
        $el.on('click', '.ddp-upload-cell', function () {
            self._openNativePicker();
        });

        // 부분접근 → 모두 허용 유도. 앱이 OS 권한 재선택 창을 띄우고 끝나면 첫 페이지를 다시 보낸다
        $el.on('click', '.ddp-pick-more', function () {
            if (self.loading || self.committing) return;
            // 시스템 창이 뜨기 전에 무엇을 골라야 하는지 먼저 알린다 (창 자체는 앱/OS 가 그린다)
            $j.Ui.Toast.show('다음 화면에서 \'모두 허용\'을 선택하세요.', { type: 'info', timeout: 2500 });
            if (!$j.F.App.listPhotos({ limit: PAGE_SIZE, reselect: true })) {
                $j.Ui.Toast.show('warning', '이 버전에서는 사진 권한 재선택을 지원하지 않습니다.');
                return;
            }
            self._pendingReset = true;
            self.loading = true;
            self._renderMore();
            self._startTimer(false);
        });

        // 폴백(구앱·권한거부) — 네이티브 피커 열기
        $el.on('click', '.ddp-native', function () {
            self._openNativePicker();
        });

        // 푸터 확장 슬롯(호출부 주입 HTML) 클릭 위임 — 피커는 내용을 모르고 호출부로만 넘긴다
        if (typeof self.data?.onFooterClick === 'function') {
            $el.on('click', '.ddp-foot-extra', function (e) { self.data.onFooterClick(e, self); });
        }

        $el.on('click', '.ddp-btn-cancel', function () { self._backToHub(); });
        $el.on('click', '.ddp-btn-submit', function () { self._commitSelected(); });
    }

    // ─── 커밋 ────────────────────────────────────────
    _commitSelected() {
        if (this.committing) return;
        const tokens = Array.from(this.selected.keys());
        if (!tokens.length) { $j.Ui.Toast.show('warning', '사진을 선택하세요.'); return; }
        // 촬영한 사진이 올라가는 중이면 막는다 — 두 업로드의 회신은 같은 'upload:image' 로 와서 어느 쪽인지 가릴 수 없다
        // (태그가 뒤바뀌거나 같은 사진이 두 번 등록될 수 있음). 앱은 카메라 취소 때 아무것도 안 보내므로 끝을 기다리지 않고 15초만 막는다.
        if (this._camAt && Date.now() - this._camAt < CAM_GUARD_MS) {
            $j.Ui.Toast.show('warning', '방금 촬영한 사진을 올리는 중일 수 있어요. 잠시 뒤 다시 눌러 주세요.');
            return;
        }

        this.committing = true;
        this._renderFooter();
        $j.Common.Util.loadingOverlay(true);

        // token 순서대로 태그를 스냅샷 — 앱은 token 순서대로 업로드·회신하므로 결과와 같은 순서로 짝짓는다.
        // 추천 셀은 제안 태그, 최근 격자 셀은 활성 칩.
        this._commitTags = tokens.map(t => this.itemTags.get(t) || this.activeTag);

        // 완료 통지는 기존 callbackUploadImage → 'upload:image' 를 그대로 탄다.
        // addPhotoHere 를 거치지 않는 경로라 context 는 여기서 직접 선점한다.
        // passthrough 면 호출부 context 를 세워야 기존 핸들러가 결과를 받는다.
        $j.F.Upload.setContext({ context: this.uploadContext, tag: this.uploadTag, id: this.uploadId });

        const accepted = $j.F.App.uploadSelectedPhotos(this.uploadTag, this.uploadId, tokens);
        if (!accepted) {
            this.committing = false;
            $j.Common.Util.loadingOverlay(false);
            this._renderFooter();
            this._useNativeFallback(this._noApp()
                ? '앱에서만 선택한 사진을 올릴 수 있습니다. 파일을 직접 선택해 주세요.'
                : '이 버전에서는 선택한 사진을 올릴 수 없습니다.', true);
            return;
        }
        this._startCommitTimer();
    }

    /**
     * 업로드가 끝난 파일 처리. 커밋은 호출부에 위임한다(onCommit) — 피커는 어느 도메인에 저장되는지 모른다.
     * onCommit 이 없으면 passthrough: 호출부의 기존 upload:image 핸들러가 결과를 받고, 피커는 시트만 닫는다.
     */
    _onUploaded(files) {
        const onCommit = this.data?.onCommit;
        if (typeof onCommit !== 'function') { this._finishCommit(true); return; }

        // 커밋 후: BS(hub)로 열린 경우만 허브로 복귀. 인라인 전체격자(onClose)는 시트를 닫지 않고 그대로 둔다
        // (고른 최근사진이 격자에 그대로 보여 확인 가능. 시트 X 로 닫으면 일지가 BS:close 로 등록분을 다시 읽음).
        const backAfter = !!this.data?.hub;
        const tags = this._commitTags;
        this._commitTags = null;
        // 촬영 셀 회신(tags 없음)은 선택 커밋이 아니다 — 체크해 둔 최근 사진을 '올라간 것'으로 치거나 선택을 풀지 않는다
        const fromCamera = !tags;
        if (fromCamera) this._camAt = 0;
        // 올라간 token — 회신 장수가 선택 장수에 못 미치면(일부 실패) 어느 장이 빠졌는지 모르므로 추천을 건드리지 않는다
        const committed = (!fromCamera && files.length >= this.selected.size) ? Array.from(this.selected.keys()) : [];
        Promise.resolve(onCommit(files, { tag: this.activeTag, id: this.uploadId, tags }))
            .then(() => this._finishCommit(backAfter, committed, fromCamera))
            .catch(() => {
                this.committing = false;
                $j.Common.Util.loadingOverlay(false);
                this.selected.clear();
                this._renderFooter();
            });
    }

    _finishCommit(backToHub, committed = [], keepSelected = false) {
        this.committing = false;
        $j.Common.Util.loadingOverlay(false);
        if (!keepSelected) this.selected.clear();
        if (backToHub) { this._backToHub(); return; }
        this._afterCommitInline(committed);
    }

    /**
     * 시트가 남는 인라인 격자의 등록 뒤 정리. 그냥 두면 추천 6장이 번호(선택 표시)를 단 채 그대로 남고 버튼만 꺼져
     * "올라간 건가, 또 눌러야 하나" 가 된다(2026-10-02 영상 0:33). 올라간 사진은 이제 기첨부이므로 추천에서 빼고
     * 남은 추천을 선택 없이 다시 그린다(_applySuggest). 최근 격자의 셀은 그대로 두되 선택 표시만 지운다.
     */
    _afterCommitInline(committed) {
        const done = new Set(committed);
        if (done.size) {
            const norm = v => String(v || '').replace(/\D/g, '');
            for (const t of done) {
                const it = this._findItem(t);
                if (it && it.date) this.excludeDates.add(norm(it.date));
            }
            if (this.sg && this.sg.state === 'found') {
                const left = this.sg.items.filter(it => !done.has(it.token));
                const reg = (this.sg.registered || 0) + (this.sg.items.length - left.length);
                this.sg = left.length
                    ? { state: 'found', items: left, reason: '', excluded: this.sg.excluded || 0, registered: reg }
                    : { state: 'empty', items: [], reason: '', excluded: (this.sg.excluded || 0) + reg, registered: reg };
            }
        }
        this._applySuggest();   // 추천 구역·최근 격자(선택 표시 해제)·푸터를 다시 그린다
    }

    /**
     * 피커를 닫고 복귀.
     *  - hub 있음(BS 로 열린 경우): 허브를 다시 연다 (BS 싱글톤).
     *  - onClose 있음(허브 안 인라인으로 열린 경우): 호출부가 시트 전체를 닫는다.
     *  - 둘 다 없음(passthrough·프로필 등): 자기 시트만 닫는다.
     */
    _backToHub() {
        const hub = this.data?.hub;
        if (hub && hub.target) { $j.Ui.BS.open(hub); return; }
        if (typeof this.data?.onClose === 'function') { this.data.onClose(); return; }
        this.parent?.close?.();
    }

    // ─── 유틸 ────────────────────────────────────────
    _esc(s) {
        return String(s == null ? '' : s).replace(/[&<>"']/g, c => ({
            '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
        })[c]);
    }
}
